import {
    DataSnapshot,
    get,
    onChildAdded,
    onChildChanged,
    onChildRemoved,
    push,
    ref,
    serverTimestamp,
    set,
    update,
} from 'firebase/database'
import { db, functions } from './configs'
import {
    BookingPlacementDetails,
    BookingReplacementDetails,
    BookingSchoolNotes,
    SchoolBookingKeys,
    SchoolBookingType,
} from './types-bookings'
import {
    BookableTeacherAvailability,
    BookableTeachersForDate,
    SchoolAutoReplaceSetting,
    SchoolDetails,
    SchoolInformation,
    SchoolProfile,
    TeachingEnvSupport,
} from './types-school'
import { httpsCallable } from 'firebase/functions'
import {
    SendRequestArgs,
    SendRequestResponse,
    CreateBulkBookingRequest,
    CreateBulkBookingResponse,
    CancelBookingBySchoolRequest,
    CancelReplacementRequestProps,
} from '../schemas/api'
import dayjs, { Dayjs } from 'dayjs'
import { ReportIssueForm } from '../pages/Dashboard/schools/components/ReportIssueModal'
import { EmploymentSettings, TeacherPublicInfo } from './types-teacher'
import { TeacherEmploymentSetting } from '../redux/types'
import { IncidentReportForm } from '../pages/Dashboard/schools/components/ReportIncidentModal'
import { SchoolEvent } from '../components/Snackbar'
import { DB_REF } from './helpers'
import { logger } from '../helpers/logger'

export type LocationParams = {
    country: string
    locationState: string
}
export type BookingParams = {
    placementUid: string
    dateObj: Dayjs
    interest: TeacherEmploymentSetting
    isSupportStaff?: boolean
}
export const PLACEMENT_REF = (args: LocationParams & BookingParams) =>
    `countries/${args.country}/${
        args.locationState
    }/placements${args.interest === 'supportStaff' ? '_supportStaff' : ''}/confirmed/${args.dateObj.format('YYYY')}/${args.dateObj.format(
        'MM'
    )}/${args.dateObj.format('DD-MM-YYYY')}/${args.placementUid}`

export const TEACHER_PUBLICINFO_REF = (
    args: LocationParams & { teacherUid: string }
) =>
    `countries/${args.country}/${args.locationState}/teachers/publicInformation/${args.teacherUid}`

export const SCHOOLS_API = {
    setAutoreplaceSetting: (
        uid: string,
        key: keyof SchoolAutoReplaceSetting,
        value: boolean
    ) =>
        set(
            ref(
                db,
                `schoolInformation/${uid}/details/activeAutoReplacements/${key}`
            ),
            value ? true : null
        ),
    bookables: {
        listenForDate: (
            config: LocationParams & {
                interest: TeacherEmploymentSetting
                schoolUid: string
                queryType: 'date' | 'teacherUid'
            },
            date: string,
            callback: {
                added: (snap: DataSnapshot) => void
                changed: (snap: DataSnapshot) => void
                removed: (snap: DataSnapshot) => void
            }
        ) => {
            const thisRef = ref(
                db,
                `${DB_REF.schoolBookablePath(config)}/${date}`
            )

            return [
                onChildAdded(thisRef, callback.added),
                onChildChanged(thisRef, callback.changed),
                onChildRemoved(thisRef, callback.removed),
            ]
        },
    },
    events: {
        listen: (
            schoolUid: string,
            callback: {
                added: (snap: DataSnapshot) => void
                changed: (snap: DataSnapshot) => void
                removed: (snap: DataSnapshot) => void
            }
        ) => {
            const thisRef = ref(db, `schools/${schoolUid}/events`)
            return [
                onChildAdded(thisRef, callback.added),
                onChildChanged(thisRef, callback.changed),
                onChildRemoved(thisRef, callback.removed),
            ]
        },
        fetchOnce: async (
            schoolUid: string
        ): Promise<Record<string, SchoolEvent> | null> =>
            (await get(ref(db, `schools/${schoolUid}/events`))).val(),
    },
    eventReads: {
        listen: (
            schoolUid: string,
            userUid: string,
            callback: {
                added: (snap: DataSnapshot) => void
                removed: (snap: DataSnapshot) => void
            }
        ) => {
            const thisRef = ref(
                db,
                `schools/${schoolUid}/eventReads/${userUid}`
            )
            return [
                onChildAdded(thisRef, callback.added),
                onChildRemoved(thisRef, callback.removed),
            ]
        },

        markAsRead: (
            schoolUid: string,
            userUid: string,
            eventIds: string[]
        ) => {
            logger.debug(`marking read:`, eventIds)
            return update(
                ref(db, `schools/${schoolUid}/eventReads/${userUid}`),
                eventIds.reduce(
                    (a, c) => ({ ...a, [c]: true }),
                    {} as Record<string, boolean>
                )
            )
        },
    },
    teachers: {
        get: async <T>(
            args: LocationParams & {
                teacherUid: string
            },
            name: keyof TeacherPublicInfo
        ): Promise<T | null> =>
            (
                await get(
                    ref(db, `${TEACHER_PUBLICINFO_REF(args)}/${name}/value`)
                )
            ).val(),
        getEmploymentSetting: async (
            args: LocationParams & { teacherUid: string }
        ) =>
            (
                await get(
                    ref(db, `${TEACHER_PUBLICINFO_REF(args)}/employmentSetting`)
                )
            ).val() as EmploymentSettings | null,
        fetchCompletionRate: async (
            args: LocationParams & {
                teacherUid: string
                type: 'school' | 'childcare' | 'supportStaff'
            }
        ): Promise<number | null> =>
            (
                await get(
                    ref(
                        db,
                        `${TEACHER_PUBLICINFO_REF(args)}/completionRate/${args.type}`
                    )
                )
            ).val(),
        fetchIsFavourite: async (args: {
            teacherUid: string
            schoolUid: string
        }): Promise<boolean> =>
            (
                await get(
                    ref(
                        db,
                        `schoolInformation/${args.schoolUid}/favourites/${args.teacherUid}`
                    )
                )
            ).val() || false,
        setFavourite: (
            teacherUid: string,
            schoolUid: string,
            isFavourited: boolean
        ) =>
            set(
                ref(
                    db,
                    `schoolInformation/${schoolUid}/favourites/${teacherUid}`
                ),
                isFavourited ? isFavourited : null
            ),
    },
    businessManager: {
        setInvoiceEmail: (schoolUid: string, email: string) =>
            set(
                ref(
                    db,
                    `schoolInformation/${schoolUid}/businessManager/invoiceEmail`
                ),
                email
            ),
        updateDetails: (
            schoolUid: string,
            data: { name: string; email: string; contact: string }
        ) =>
            update(
                ref(db, `schoolInformation/${schoolUid}/businessManager`),
                data
            ),
    },
    details: {
        updateDetails: (schoolUid: string, data: SchoolDetails) =>
            update(ref(db, `schoolInformation/${schoolUid}/details`), data),
        updateBellTimesRequest: (
            schoolUid: string,
            data: {
                times: Record<string, string>
                type: SchoolBookingType
                requestedBy: string
            }
        ) =>
            push(
                // prettier-ignore
                ref( db, `adminNotifications/schoolDetailUpdates/${schoolUid}/bellTimes` ),
                {
                    ...data,
                    state: 'incomplete',
                    timeStamp: serverTimestamp(),
                }
            ),
    },
    information: {
        update: (schoolUid: string, data: Partial<SchoolInformation>) =>
            update(ref(db, `schoolInformation/${schoolUid}`), data),
    },
    profile: {
        updateProfile: (schoolUid: string, data: Partial<SchoolProfile>) =>
            update(ref(db, `schoolInformation/${schoolUid}/profile`), data),
        updateTeachingEnvSupportSetup: (
            schoolUid: string,
            data: Partial<TeachingEnvSupport>
        ) =>
            update(
                ref(
                    db,
                    `schoolInformation/${schoolUid}/profile/teachingEnvSupport`
                ),
                data
            ),
    },
} as const

//#region Queries
export const listenBookingKeys = (
    country: string,
    locationState: string,
    schoolId: string,
    year: string,
    callback: {
        added: (snap: DataSnapshot) => void
        changed: (snap: DataSnapshot) => void
        removed: (snap: DataSnapshot) => void
    }
) => {
    const thisRef = ref(
        db,
        `countries/${country}/${locationState}/schools/placements/${schoolId}/${year}`
    )
    return [
        onChildAdded(thisRef, callback.added),
        onChildChanged(thisRef, callback.changed),
        onChildRemoved(thisRef, callback.removed),
    ]
}

export const fetchBookingKeys = async (
    country: string,
    locationState: string,
    schoolId: string
): Promise<SchoolBookingKeys | null> =>
    (
        await get(
            ref(
                db,
                `countries/${country}/${locationState}/schools/placements/${schoolId}`
            )
        )
    ).val()

export const fetchBookableTeachers = async (
    country: string,
    locationState: string,
    schoolId: string
): Promise<BookableTeachersForDate | null> => {
    return (
        await get(
            ref(
                db,
                `countries/${country}/${locationState}/schools/availableTeachers/${schoolId}`
            )
        )
    ).val()
}

export const fetchBookableTeacherForDate = async (
    country: string,
    locationState: string,
    schoolId: string,
    dateStr: string
): Promise<BookableTeachersForDate | null> => {
    return (
        await get(
            ref(
                db,
                `countries/${country}/${locationState}/schools/availableTeachers/${schoolId}/${dateStr}`
            )
        )
    ).val()
}

export const fetchTeacherBookableDates = async (
    country: string,
    locationState: string,
    schoolId: string,
    teacherUid: string
): Promise<BookableTeacherAvailability | null> => {
    return (
        await get(
            ref(
                db,
                `countries/${country}/${locationState}/schools/bookableTeacherAvailability/${schoolId}/${teacherUid}`
            )
        )
    ).val()
}

export type TeacherSimpleData = {
    profilePictureURL: string | null
    firstName: string
    lastName: string
    completionRate?: number | null
}
export const fetchTeacherSimpleData = async (
    country: string,
    locationState: string,
    teacherUid: string,
    completionRate?: boolean
): Promise<TeacherSimpleData> => {
    // prettier-ignore
    const [pic, first, last] = await Promise.all([
                        get( ref( db, `${TEACHER_PUBLICINFO_REF({country, locationState, teacherUid})}/profilePicture/value` ) ),
                        get( ref( db, `${TEACHER_PUBLICINFO_REF({country, locationState, teacherUid})}/firstName/value` ) ),
                        get( ref( db, `${TEACHER_PUBLICINFO_REF({country, locationState, teacherUid})}/lastName/value` ) ),
                    ])
    let cr: number | null | undefined = undefined
    if (completionRate) {
        cr = // prettier-ignore
            (
                await get(
                    ref(
                        db,
                        `${TEACHER_PUBLICINFO_REF({
                            country,
                            locationState,
                            teacherUid,
                        })}/completionRate/school`
                    )
                )
            ).val() as number | null
    }
    return {
        profilePictureURL: pic.val(),
        firstName: first.val() || '',
        lastName: last.val() || '',
        completionRate: cr,
    }
}

export const fetchTeacherProfile = async (
    country: string,
    locationState: string,
    teacherUid: string
): Promise<TeacherPublicInfo> => {
    return (
        await get(
            ref(
                db,
                TEACHER_PUBLICINFO_REF({ country, locationState, teacherUid })
            )
        )
    ).val()
}

export const fetchBookingSchoolNotes = async (
    args: LocationParams & BookingParams
): Promise<BookingSchoolNotes | null> => {
    return (await get(ref(db, `${PLACEMENT_REF(args)}/schoolNotes`))).val()
}

export const fetchBookingReplacementDetails = async (
    args: LocationParams & BookingParams
): Promise<BookingReplacementDetails | null> => {
    return (
        await get(ref(db, `${PLACEMENT_REF(args)}/replacementDetails`))
    ).val()
}
//#endregion Queries

//#region Mutations

export const sendRequests = async (
    args: SendRequestArgs
): Promise<SendRequestResponse> => {
    const res = await httpsCallable<SendRequestArgs, SendRequestResponse>(
        functions,
        'sendRequests'
    )(args)
    return res.data
}

export const createBookings = async (
    args: CreateBulkBookingRequest
): Promise<CreateBulkBookingResponse> => {
    const res = await httpsCallable<
        CreateBulkBookingRequest,
        CreateBulkBookingResponse
    >(
        functions,
        'createBulkBooking'
    )(args)
    return res.data
}

export const cancelBooking = (args: CancelBookingBySchoolRequest) => {
    return httpsCallable<CancelBookingBySchoolRequest, { success: true }>(
        functions,
        'cancelBookingBySchool'
    )(args)
}

export const cancelTeachingOpportunity = (
    args: CancelReplacementRequestProps
) => {
    return httpsCallable<CancelReplacementRequestProps, { success: true }>(
        functions,
        'cancelReplacementRequest'
    )(args)
}

export const createIncidentReport = (
    args: LocationParams & BookingParams & { report: IncidentReportForm }
) => {
    return push(ref(db, `${PLACEMENT_REF(args)}/schoolIncident`), {
        ...args.report,
        time: args.report.time.format('hh:mm A'),
        timeStampReported: dayjs.tz().format('DD-MM-YYYY HH:mm:ss'),
        state: 'submitted',
    })
}

export const createIssueReport = async (
    args: LocationParams &
        BookingParams & {
            report: ReportIssueForm
            raisedBy: string
            raisedByName: string
        }
) => {
    const id = await push(ref(db, `${PLACEMENT_REF(args)}/schoolIssues`), {
        ...args.report,
        raisedBy: args.raisedBy,
        raisedByName: args.raisedByName,
        timeStamp: dayjs().format('DD-MM-YYYY HH:mm:ss'),
    })
    return id.key
}
export const updateBookingPlacementDetails = (
    args: LocationParams &
        BookingParams & { data: Partial<BookingPlacementDetails> }
) => {
    return update(ref(db, `${PLACEMENT_REF(args)}/placementDetails`), args.data)
}

export const updateBookingSchoolNotes = (
    args: LocationParams & BookingParams & { data: Partial<BookingSchoolNotes> }
) => {
    return update(ref(db, `${PLACEMENT_REF(args)}/schoolNotes`), args.data)
}
//#endregion Mutations
